import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react'
import styles from './dateSelectFields.module.scss'
import globalStyles from '@/styles/components.module.scss'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { useDialog } from '@/hooks/useDialog/useDialog'
import SelectMultipleDateDialog from '../SelectMultipleDateDialog/selectMultipleDateDialog'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import { ClosingDay } from '@/types/ClosingDays'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import { DAY_FORMAT } from '../../../models/constants'
import { getEditionLimitDateFromToday } from '@/modules/helper/editionLimitHelper'
import { useTranslation } from 'react-i18next'
import { DateSelection, ListSelection, RangeSelection, SingleSelection } from '../../../models/dateSelection'
import moment from 'moment-timezone'

interface HandleCheckFunction {
    handleCheckChooseDate(): void
}

interface SingleDateSelectProps {
    dates: SingleSelection
    setDates(dates: DateSelection): void
    error?: boolean
}

type RangeDateSelectFieldProps = HandleCheckFunction & {
    dates: RangeSelection
    setDates(dates: DateSelection): void
    error?: boolean
}

type ListDateSelectFieldProps = HandleCheckFunction & {
    dates: ListSelection
    setDates(dates: DateSelection): void
    shouldOpenDateDialog: boolean
    setShouldOpenDateDialog(value: boolean): void
    calendarEvents: EventCalendar[]
    error?: boolean
}

interface DateSelectProps {
    dates: DateSelection
    setDates(dates: DateSelection): void
    calendarEvents: EventCalendar[]
    error?: boolean
}

const DateSelectFields: FunctionComponent<DateSelectProps> = (props: DateSelectProps) => {
    const [shouldOpenDateDialog, setShouldOpenDateDialog] = useState<boolean>(false)
    function handleCheckChooseDate(): void {
        const { setDates } = props
        switch (props.dates.mode) {
            case 'range': {
                setShouldOpenDateDialog(true)
                setDates({ mode: 'list', dates: [props.dates.startDate] })
                break
            }
            case 'list': {
                const resolvedDate: string = props.dates.dates[0] || moment().format(DAY_FORMAT)
                setDates({ mode: 'range', startDate: resolvedDate, endDate: resolvedDate })
                break
            }
        }
    }
    switch (props.dates.mode) {
        case 'range': {
            return (
                <RangeDateSelectFields
                    dates={props.dates}
                    setDates={props.setDates}
                    handleCheckChooseDate={handleCheckChooseDate}
                    error={props.error}
                />
            )
        }
        case 'list': {
            return (
                <ListDateSelectField
                    dates={props.dates}
                    setDates={props.setDates}
                    calendarEvents={props.calendarEvents}
                    handleCheckChooseDate={handleCheckChooseDate}
                    shouldOpenDateDialog={shouldOpenDateDialog}
                    setShouldOpenDateDialog={setShouldOpenDateDialog}
                    error={props.error}
                />
            )
        }
        case 'single': {
            return <SingleDateSelectField dates={props.dates} setDates={props.setDates} error={props.error} />
        }
    }
}

const RangeDateSelectFields: FunctionComponent<RangeDateSelectFieldProps> = (props: RangeDateSelectFieldProps) => {
    const { t } = useTranslation()
    const minDate: string = getEditionLimitDateFromToday().format(DAY_FORMAT)
    const { dates, setDates, handleCheckChooseDate } = props
    const { startDate, endDate } = dates

    function setStartDate(value: string): void {
        setDates({ mode: 'range', startDate: value, endDate })
    }

    function setEndDate(value: string): void {
        setDates({ mode: 'range', startDate, endDate: value })
    }

    return (
        <>
            <div className={styles.Input}>
                <DatePickerComponent
                    value={startDate}
                    setValue={setStartDate}
                    label={t('dateStart')}
                    minDate={minDate}
                    className={styles.UserInput}
                    error={props.error}
                />
                <DatePickerComponent
                    value={endDate}
                    setValue={setEndDate}
                    label={t('dateEnd')}
                    className={styles.UserInput}
                    minDate={startDate}
                    error={props.error}
                />
            </div>
            <div className={styles.CheckboxContainer}>
                <Checkbox
                    text={t('chooseDate')}
                    checked={false}
                    onChange={handleCheckChooseDate}
                    className={styles.Checkbox}
                />
            </div>
        </>
    )
}

const ListDateSelectField: FunctionComponent<ListDateSelectFieldProps> = (props: ListDateSelectFieldProps) => {
    const { t } = useTranslation()
    const { dates, setDates, calendarEvents, handleCheckChooseDate } = props
    const { dates: dateList } = dates

    function setDateList(list: string[]): void {
        setDates({ mode: 'list', dates: list })
    }

    const { openDialog: openMultipleDateDialog, dialog: multipleDateDialog } = useDialog({
        dialogTitle: false,
        Component: SelectMultipleDateDialog,
        props: (handleClose) => ({
            handleClose,
            dateList,
            setDateList,
            startDate: dateList[0] || moment().format(DAY_FORMAT),
            closingDays: calendarEvents
                .filter((event) => event.type === CalendarEventType.CLOSING_DAY)
                .map((event) => {
                    return event.slots as ClosingDay
                })
        })
    })

    useEffect(() => {
        if (props.shouldOpenDateDialog) {
            openMultipleDateDialog()
            props.setShouldOpenDateDialog(false)
        }
    }, [])

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date: string) => date !== dateToRemove))
    }

    return (
        <>
            <div className={styles.Input}>
                <Autocomplete
                    multiple
                    options={dateList}
                    limitTags={3}
                    value={dateList}
                    onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                    renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                        value.map((option: string, index: number) => {
                            const { key, ...tagProps } = getTagProps({ index })
                            return (
                                <div key={key}>
                                    <Chip label={option} {...tagProps} onDelete={() => handleDateRemove(option)} />
                                </div>
                            )
                        })
                    }
                    renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                            {...params}
                            variant='outlined'
                            rows={1}
                            multiline={true}
                            label={t('dateList')}
                            error={props.error}
                        />
                    )}
                    className={`${styles.Autocomplete} ${props.error ? globalStyles.FieldInError : ''}`}
                    onOpen={openMultipleDateDialog}
                />
            </div>
            <div className={styles.CheckboxContainer}>
                <Checkbox
                    text={t('chooseDate')}
                    checked={true}
                    onChange={handleCheckChooseDate}
                    className={styles.Checkbox}
                />
            </div>
            {multipleDateDialog}
        </>
    )
}

const SingleDateSelectField: FunctionComponent<SingleDateSelectProps> = (props: SingleDateSelectProps) => {
    const { t } = useTranslation()
    const minDate: string = getEditionLimitDateFromToday().format(DAY_FORMAT)
    const { dates, setDates } = props

    function setDate(value: string): void {
        setDates({ mode: 'single', date: value })
    }

    return (
        <DatePickerComponent
            value={dates.date}
            setValue={setDate}
            label={t('date')}
            minDate={minDate}
            className={styles.UserInput}
            error={props.error}
        />
    )
}

export default DateSelectFields
